import React from 'react';
import { Link } from 'react-router-dom';

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-primary-dark text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4">Delete Account & Data</h1>
          <p className="text-xl text-white/90">
            We're sorry to see you go. Here's how to permanently delete your account and all associated data.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <span className="text-4xl">🗑️</span>
            </div>
          </div>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Delete Your Account</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To delete your account and all associated data, please send an email to{' '}
              <a href="mailto:support@actpaws.com?subject=Delete My Account" className="text-primary font-semibold hover:underline">
                support@actpaws.com
              </a>{' '}
              with the subject <strong className="text-gray-900">"Delete My Account"</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We will verify your identity and remove your data within 7 days.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Will Be Deleted</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you delete your account, the following information will be permanently removed:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your user account and profile information</li>
              <li>All pet profiles and associated data</li>
              <li>Health records, activity logs, and tracking data</li>
              <li>Photos and media you've uploaded</li>
              <li>Progress reports and insights</li>
              <li>Subscription and payment information</li>
              <li>All other data associated with your account</li>
            </ul>
          </section>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
            <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center">
              <span className="mr-2">⚠️</span>
              This Action Cannot Be Undone
            </h3>
            <p className="text-red-700 leading-relaxed">
              Once your account is deleted, all your data will be permanently removed from our 
              systems and cannot be recovered. Please make sure you have exported or backed up 
              any information you want to keep before requesting account deletion.
            </p>
          </div>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Verification Process</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To protect your account security, we will verify your identity before processing the 
              deletion request. This may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Verifying the email address associated with your account</li>
              <li>Confirming your identity through additional security questions</li>
              <li>Ensuring the request comes from the account owner</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Processing Time</h2>
            <p className="text-gray-700 leading-relaxed">
              After we verify your identity, we will process your account deletion request within 
              <strong className="text-gray-900"> 7 business days</strong>. You will receive a confirmation email once your 
              account and data have been permanently deleted.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Alternative Options</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you're experiencing issues with the app or have concerns, we'd love to help! 
              Consider these alternatives before deleting your account:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Contact our support team for assistance</li>
              <li>Pause your subscription instead of deleting</li>
              <li>Export your data for backup before making a decision</li>
            </ul>
          </section>

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <a
              href="mailto:support@actpaws.com?subject=Delete My Account"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-full text-center transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Send Deletion Request Email
            </a>
            <Link
              to="/contact"
              className="flex-1 btn-secondary text-center"
            >
              Contact Support Instead
            </Link>
          </div>

          <section className="pt-6 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Help?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about the deletion process or need assistance, please don't 
              hesitate to contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 space-y-3">
              <p className="text-gray-700">
                <strong className="text-gray-900">Email:</strong>{' '}
                <a href="mailto:support@actpaws.com" className="text-primary hover:underline">
                  support@actpaws.com
                </a>
              </p>
              <p className="text-gray-700">
                <strong className="text-gray-900">Phone:</strong>{' '}
                <a href="tel:+919980363932" className="text-primary hover:underline">
                  +91 9980363932
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
